import { anchorLabel, entryProblem, locatedAnchor, pendingEntry, sameAddress } from '../../../shared/routeAnchor';
import type { MeetingEntry, RouteAnchor } from '../../../shared/routeAnchor';
import { meetingPointForm, reasonText } from './meetingPoint';
import type { MeetingForm } from './meetingPoint';
import { pickFor, rememberPick, restartPick, twoHopState } from './routePickState';
import type { PickDraft } from './routePickState';
import { beginRouteLead } from '../data/routeLead';
import { pacificToday } from '../../../shared/dates';
import type { PosSystem } from '../../../shared/enums';
import type { CorridorPlace, Lead } from '../../../shared/types';
import { shell } from '../app';
import { ROUTE_RADIUS_M } from '../config';
import { ApiError, NetworkError } from '../data/api';
import { createRoutePlaces } from '../data/routePlaces';
import type { RoutePlaceRow } from '../data/routePlaces';
import {
  clearRoute,
  fetchCorridor,
  fingerprintPending,
  googlePlaceId,
  loadRoute,
  navigateUrl,
  placeFromStop,
  ROUTE_CHANGED,
  saveRoute,
  toRouteStop,
  unwatched,
} from '../data/corridor';
import { locatedStop } from '../../../shared/routeRetention';
import type { RouteStop, StoredRoute } from '../data/corridor';
import { queueSignalPatch } from '../data/signals';
import { createLead, getSnapshot, listLeads, onStoreChange } from '../data/store';
import { kickSync } from '../data/sync';
import { clockText } from '../logic/daily';
import { todayLocal } from '../logic/dates';
import {
  canAutoCreateLead,
  classifyPlace,
  formatDistance,
  inferBusinessType,
  isHidden,
  keepLeadLink,
  keepOrder,
  leadIdForStop,
  orderStops,
} from '../logic/route';
import type { PinKind } from '../logic/route';
import { btn, empty, linkBtn } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { POS_LABELS, STAGE_LABELS } from '../ui/labels';
import { anchorIcon, createMap, loadGoogleMaps, onMapsAuthFailure, pinIcon } from '../ui/maps';
import { navigate } from '../ui/router';
import { suggestBox } from '../ui/suggestBox';
import type { SuggestBox } from '../ui/suggestBox';
import type { View } from '../ui/router';

/** Route tab (M2): anchor search → corridor scouting on a map → greedy day route → per-stop
 *  navigation + quick-log. The built route is the account's day route (D84, amending D27): kept on
 *  the device so the tab works offline, queued to the server through the outbox, last writer wins. */

const DETECTED_TO_POS: Record<CorridorPlace['detected_pos'], PosSystem> = {
  square: 'square',
  toast: 'toast',
  clover: 'clover',
  spoton: 'spoton',
  touchbistro: 'touchbistro',
  lightspeed: 'lightspeed',
  shopify: 'shopify',
  chownow: 'chownow',
  other: 'other',
  unknown: 'unknown',
  pending: 'unknown',
};

const POS_BADGE_TEXT: Record<string, string> = {
  pending: 'checking…',
  unknown: 'POS unknown',
};

const posBadge = (pos: CorridorPlace['detected_pos'], kind?: PinKind) =>
  span(
    `posbadge posbadge--${kind ?? 'prospect'} posbadge--${pos}`,
    POS_BADGE_TEXT[pos] ?? POS_LABELS[DETECTED_TO_POS[pos]],
  );

const PLACES_DENIED =
  'Places search was denied — check the browser key restrictions in the Google Cloud console.';

/** The meeting-point search's states (CAY-81 spec §3.5). The offline line points at the saved
 *  route only when the Resume button is on screen (CAY-90 follow-up). */
const SEARCH_PICKING = 'Finding the spot…';
const searchCopy = (hasSavedRoute: boolean) => ({
  searching: 'Searching…',
  noMatch: (q: string) => `No match near Orange County for "${q}" — try the street and city.`,
  offline: hasSavedRoute
    ? 'No signal — search needs a connection. Your saved route still opens below.'
    : 'No signal — search needs a connection.',
  error: 'Search failed — try again.',
  picking: SEARCH_PICKING,
});

export { PICK_STATUS_MS } from './routePickState';
const CITY_ZIP_TEXT = 'Add the city and ZIP, then Resolve meeting point.';

const anchorEntry = (a: RouteAnchor | undefined): MeetingEntry | undefined =>
  a && (a.kind === 'unresolved' || a.kind === 'census_entry') ? a.entry : undefined;
const sameEntry = (a: MeetingEntry | undefined, b: MeetingEntry | undefined) => (!a && !b) || (!!a && !!b && sameAddress(a, b));
/** A pending save starts the two-hop clock (CAY-115); a pick also keeps its name, in memory only. */
const rememberSave = (anchor: RouteAnchor, name?: string) => {
  if (anchor.kind === 'unresolved' && anchor.reason === 'pending' && anchor.entry) rememberPick(anchor.entry, name, Date.now());
};

const friendlyError = (err: unknown): string => {
  if (err instanceof NetworkError) return 'No signal — the Route tab needs a connection to scout.';
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
};

export const routeView: View = ({ root }) => {
  shell.setChrome(true);
  const host = h('div', { class: 'page page--route' });
  root.appendChild(host);

  // ---------------------------------------------------------------- shared state
  let stored: StoredRoute | null = loadRoute();
  let places: CorridorPlace[] = [];
  const selected = new Map<string, CorridorPlace>();
  let showHidden = false;
  let map: google.maps.Map | null = null;
  const markers = new Map<string, google.maps.Marker>();
  let anchorMarker: google.maps.Marker | null = null;
  let disposed = false;
  let storeOff: (() => void) | null = null;
  // Bumped on every mode switch: a scout still loading for an old address must not draw its pins
  // on the new map, overwrite `places`, or raise its errors on the new screen.
  let scoutSeq = 0;
  /** What the tab shows; a route the sync brings redraws it unless a scout is being edited. */
  let mode: 'search' | 'scout' | 'stops' = 'search';

  /** The corridor's copy of a place knows no lead when that lead has no place_id (typed by hand):
   *  keep the link the current pick or the stored stop carries. */
  const withLeadLink = (p: CorridorPlace): CorridorPlace =>
    keepLeadLink(p, selected.get(p.place_id) ?? stored?.stops.find((s) => s.place_id === p.place_id));

  /** A pick as a stop, keeping the follow-up note and lead link the stored route had for it. */
  const stopOf = (p: CorridorPlace): RouteStop =>
    toRouteStop(withLeadLink(p), stored?.stops.find((s) => s.place_id === p.place_id)?.note);

  const persistScout = () => {
    if (!stored) return;
    unwatched(saveRoute({ ...stored, stops: [...selected.values()].map(stopOf), built: false }));
  };

  // ---------------------------------------------------------------- error banner
  const banner = h('div', { class: 'banner', hidden: true, role: 'alert' });
  const showBanner = (text: string) => {
    banner.textContent = text;
    banner.hidden = false;
  };
  const authOff = onMapsAuthFailure(() =>
    showBanner(
      `Google Maps rejected this page. Add ${location.origin} to the browser key's website restrictions ` +
        '(Google Cloud console → Credentials) — this is the RefererNotAllowedMapError case.',
    ),
  );

  // ================================================================ mode 1: search (no anchor)
  // The app's own combobox over Google's Places library (D122, amending D28). The source (and its
  // session token) belongs to this mount: leaving the Route tab abandons the session, and coming
  // back starts a new one. A session also ends on a pick or after three idle minutes.
  const meetingPoints = createRoutePlaces();
  let searchBox: SuggestBox | null = null;
  /** The rep typed in the box since the last pick: its text and line are theirs, not the state's. */
  let boxTouched = false;
  /** The meeting-point form on screen (search or stops), the draft it shows, and the address it was drawn for. */
  let liveForm: MeetingForm | null = null;
  let liveDraft: PickDraft | undefined;
  let liveEntry: MeetingEntry | undefined;
  let pickTimer: ReturnType<typeof setTimeout> | undefined;
  const closeSearch = () => {
    clearTimeout(pickTimer);
    pickTimer = undefined;
    searchBox?.dispose();
    searchBox = null;
    liveForm = null;
    liveDraft = undefined;
  };

  /** The two-hop lines (CAY-115), applied in place to the box and the form, and the next change timed. */
  const refreshTwoHop = () => {
    clearTimeout(pickTimer);
    pickTimer = undefined;
    if (disposed) return;
    const anchor = stored?.anchor;
    const view = twoHopState(anchor, pickFor(anchor), navigator.onLine !== false, Date.now(), liveDraft);
    liveForm?.refresh(view.form);
    if (searchBox && !boxTouched) {
      if (view.box.value !== null) searchBox.input.value = view.box.value;
      searchBox.setStatus(view.box.status, view.box.tone);
    }
    if (view.expiresAt !== null) pickTimer = setTimeout(refreshTwoHop, Math.max(0, view.expiresAt - Date.now()));
  };
  /** The search screen stays (box, focus, scroll, typed fields) when the route that changed under it
   *  still lands on search for the same address: an acknowledgement or a same-address re-submit only
   *  changes its lines. That is what stops a second tap from emptying the box and moving the page. */
  const searchStays = () =>
    mode === 'search' && !!liveForm && !liveDraft && !stored?.stops.length && !locatedAnchor(stored) && sameEntry(anchorEntry(stored?.anchor), liveEntry);
  const onConnection = (ev: Event) => {
    if (ev.type === 'online') restartPick(Date.now()); // the sync sends the queued PUT now
    refreshTwoHop();
  };
  window.addEventListener('online', onConnection);
  window.addEventListener('offline', onConnection);

  /** One save path for the typed form and a search pick: the new anchor replaces the old one and
   *  the stops and notes stay (D120). An acknowledgement that lands during the save is honoured. */
  const saveAnchor = async (anchor: RouteAnchor, name?: string) => {
    const current = stored ? loadRoute(stored.day) ?? stored : null;
    const next: StoredRoute = current ? { ...current, anchor } : { anchor, stops: [], built: false, created_at: new Date().toISOString() };
    await saveRoute(next);
    rememberSave(anchor, name);
    stored = loadRoute(next.day);
    selected.clear();
    if (disposed) return;
    if (searchStays()) refreshTwoHop();
    else if (stored?.stops.length) renderStops();
    else if (locatedAnchor(stored)) void renderScout();
    else renderSearch();
  };

  /** A pick fills the D120 entry from Google's address components; the server's Census lookup
   *  locates it (hop 2), and its acknowledgement redraws the tab through ROUTE_CHANGED. A pick
   *  Google cannot give a full building address for pre-fills the form and saves nothing. */
  const pickMeetingPoint = async (row: RoutePlaceRow): Promise<boolean> => {
    let picked: Awaited<ReturnType<typeof meetingPoints.resolve>>;
    try {
      picked = await meetingPoints.resolve(row);
    } catch (err) {
      shell.toast(friendlyError(err), { kind: 'error' });
      return false;
    }
    if (disposed) return true;
    boxTouched = false; // the pick is the box's text again, whatever was typed to find it
    if ('entry' in picked) {
      try {
        // Between the hops the box keeps the picked name, online or offline (CAY-115); the lines
        // come from twoHopState, through the redraw or the in-place refresh.
        await saveAnchor(pendingEntry(picked.entry), row.main);
      } catch {
        shell.toast('Could not queue the meeting point. Please retry.', { kind: 'error' });
        return false;
      }
      return true;
    }
    const message =
      picked.missing === 'city_zip'
        ? CITY_ZIP_TEXT
        : reasonText(entryProblem({ street: '', unit: '', city: '', state: '', zip: '', label: '', ...picked.draft }) ?? 'invalid_street');
    renderSearch({ entry: picked.draft, message, name: row.main, missing: picked.missing });
    return true;
  };

  const renderSearch = (draft?: PickDraft) => {
    mode = 'search';
    scoutSeq++;
    shell.setTitle('Route');
    closeSearch();
    const resumable = !!stored?.stops.length;
    searchBox = suggestBox<RoutePlaceRow>({
      ariaLabel: 'Meeting address or business',
      placeholder: 'Business name or address',
      listLabel: 'Matching places',
      copy: searchCopy(resumable),
      fetch: meetingPoints.suggest,
      pick: pickMeetingPoint,
      onDenied: () => showBanner(PLACES_DENIED),
    });
    // The resting line belongs to the pick (D122 amendment): typing starts a new search, so the
    // first keystroke lets it go rather than leaving it under the fresh rows. Escape keeps it.
    const box = searchBox;
    boxTouched = false;
    box.input.addEventListener('input', () => {
      boxTouched = true;
      box.setStatus('');
    });
    liveDraft = draft;
    liveEntry = anchorEntry(stored?.anchor);
    liveForm = meetingPointForm(stored?.anchor, (anchor) => saveAnchor(anchor), draft);
    replaceChildren(
      host,
      banner,
      h(
        'div',
        { class: 'route-intro' },
        h('h2', { class: 'route-intro__h' }, 'Route by meeting address'),
        h('p', { class: 'route-intro__p' }, 'Flags who is likely not on Square within ~0.6 mi.'),
      ),
      searchBox.root,
      liveForm.root,
      linkBtn('Plan tomorrow', '#/route/plan', { variant: 'primary', class: 'w-full' }),
      stored && resumable
        ? btn(`Resume saved route (${stored.stops.length} stops)`, {
            variant: 'subtle',
            class: 'w-full',
            onClick: () => {
              if (stored?.built || (stored?.stops.length && !locatedAnchor(stored))) renderStops();
              else void renderScout();
            },
          })
        : null,
    );
    refreshTwoHop();
    // Warm the library so the first keystroke is quick and a refused key shows its banner early;
    // a load failure here is silent: the search itself reports it on the status line.
    loadGoogleMaps().catch(() => undefined);
  };

  // ================================================================ mode 2: scout (map + pins)
  const kindOf = (p: CorridorPlace): PinKind => classifyPlace(p);

  const paintMarker = (p: CorridorPlace) => {
    const marker = markers.get(p.place_id);
    if (!marker) return;
    const kind = kindOf(p);
    const hide = isHidden(kind) && !showHidden && !selected.has(p.place_id);
    marker.setMap(hide ? null : map);
    marker.setIcon(pinIcon(kind, selected.has(p.place_id)));
    marker.setZIndex(selected.has(p.place_id) ? 30 : kind === 'competitor' ? 20 : 10);
  };

  const card = h('div', { class: 'placecard', hidden: true });
  const renderCard = (p: CorridorPlace) => {
    const kind = kindOf(p);
    const isSelected = selected.has(p.place_id);
    replaceChildren(
      card,
      h(
        'div',
        { class: 'placecard__text' },
        h('div', { class: 'placecard__name' }, p.name),
        h('div', { class: 'placecard__meta' }, p.formatted_address ?? ''),
        h(
          'div',
          { class: 'placecard__badges' },
          posBadge(p.detected_pos, kind),
          p.lead_id && p.stage ? span('tag tag--' + p.stage, STAGE_LABELS[p.stage]) : null,
          p.business_status === 'CLOSED_TEMPORARILY' ? span('posbadge posbadge--warn', 'temp. closed') : null,
        ),
      ),
      h(
        'div',
        { class: 'placecard__actions' },
        p.lead_id
          ? linkBtn('Open lead', `#/leads/${encodeURIComponent(p.lead_id)}`, {
              variant: 'subtle',
              size: 'sm',
            })
          : null,
        btn(isSelected ? '− Remove' : '+ Add to route', {
          variant: isSelected ? 'ghost' : 'primary',
          size: 'sm',
          onClick: () => {
            if (selected.has(p.place_id)) selected.delete(p.place_id);
            else selected.set(p.place_id, withLeadLink(p));
            persistScout();
            paintMarker(p);
            renderCard(p);
            refreshFooter();
          },
        }),
      ),
    );
    card.hidden = false;
  };

  const footer = h('div', { class: 'routefooter' });
  const refreshFooter = () => {
    const n = selected.size;
    replaceChildren(
      footer,
      span('routefooter__count', n === 0 ? 'Tap pins to pick targets' : `${n} selected`),
      btn('Build route', {
        variant: 'primary',
        disabled: n === 0,
        onClick: () => {
          if (!stored) return;
          const start = locatedAnchor(stored);
          if (!start) return void renderSearch();
          const ordered = orderStops(start, [...selected.values()].map(stopOf).filter(locatedStop));
          stored = { ...stored, stops: ordered.map((o) => o.stop), built: true };
          unwatched(saveRoute(stored));
          renderStops();
        },
      }),
    );
  };

  const applyFingerprints = (results: Record<string, string>) => {
    places = places.map((p) =>
      results[p.place_id] ? { ...p, detected_pos: results[p.place_id] as CorridorPlace['detected_pos'] } : p,
    );
    for (const id of Object.keys(results)) {
      const p = places.find((x) => x.place_id === id);
      if (p) {
        if (selected.has(id)) selected.set(id, withLeadLink(p));
        paintMarker(p);
      }
    }
    persistScout();
  };

  const renderScout = async () => {
    if (!stored) return renderSearch();
    closeSearch();
    mode = 'scout';
    const mine = ++scoutSeq;
    const stale = () => disposed || mine !== scoutSeq;
    const anchor = locatedAnchor(stored);
    if (!anchor) return stored.stops.length ? renderStops() : renderSearch();
    for (const m of markers.values()) m.setMap(null); // pins of a previous scout
    markers.clear();
    places = [];
    map = null; // set again once this scout's map exists
    shell.setTitle('Route', { subtitle: anchor.label });
    const mapEl = h('div', { class: 'mapbox', id: 'mapbox' });
    const status = h('div', { class: 'maphud mono' }, 'Scouting…');
    const hiddenToggle = h(
      'button',
      {
        class: 'maphud maphud--btn',
        type: 'button',
        'aria-pressed': String(showHidden),
        onClick: () => {
          showHidden = !showHidden;
          paintHud();
          for (const p of places) paintMarker(p);
        },
      },
      showHidden ? 'Hide Square/lost' : 'Show hidden',
    );
    /** The HUD after the corridor loads and after each fingerprint batch: with nothing left to
     *  check it reads the final count right away, and the toggle always says what it will do. */
    const paintHud = () => {
      const left = places.filter((p) => p.detected_pos === 'pending').length;
      status.textContent =
        left > 0
          ? `${places.length} places · checking ${left}…`
          : `${places.length} places · ${places.filter((p) => kindOf(p) === 'competitor').length} hot`;
      hiddenToggle.setAttribute('aria-pressed', String(showHidden));
      hiddenToggle.textContent = showHidden
        ? 'Hide Square/lost'
        : `Show hidden (${places.filter((p) => isHidden(kindOf(p))).length})`;
    };
    const newAnchor = btn('Change address', {
      variant: 'subtle',
      size: 'sm',
      onClick: () => {
        void renderSearch();
      },
    });
    replaceChildren(
      host,
      banner,
      h('div', { class: 'maphead' }, newAnchor, status, hiddenToggle),
      mapEl,
      card,
      footer,
    );
    refreshFooter();

    // restore the saved picks — also for a built route opened fresh (Today's follow-ups, back from
    // Log visit, Clear → undo): the first save below would otherwise overwrite it with no stops
    if (selected.size === 0) {
      for (const s of stored.stops.filter(locatedStop)) selected.set(s.place_id, placeFromStop(s));
      refreshFooter();
    }

    try {
      const created = await createMap(mapEl, anchor);
      if (stale()) return;
      map = created;
      anchorMarker?.setMap(null);
      const { Marker } = await google.maps.importLibrary('marker');
      if (stale()) return;
      anchorMarker = new Marker({
        map,
        position: anchor,
        icon: anchorIcon(),
        title: anchor.label,
        zIndex: 40,
      });
    } catch (err) {
      if (stale()) return;
      showBanner(friendlyError(err));
      // continue — corridor list still loads even without a map
    }

    try {
      const found = await fetchCorridor(anchor.lat, anchor.lng, ROUTE_RADIUS_M);
      if (stale()) return;
      places = found;
      paintHud();

      if (map) {
        const { Marker } = await google.maps.importLibrary('marker');
        if (stale()) return;
        for (const p of places) {
          const m = new Marker({ position: { lat: p.lat, lng: p.lng }, title: p.name });
          m.addListener('click', () => renderCard(places.find((x) => x.place_id === p.place_id) ?? p));
          markers.set(p.place_id, m);
          paintMarker(p);
        }
      }

      await fingerprintPending(places, (batch) => {
        if (stale()) return;
        applyFingerprints(batch);
        paintHud();
      });
    } catch (err) {
      if (stale()) return;
      status.textContent = 'Scout failed';
      showBanner(friendlyError(err));
    }
  };

  // ================================================================ mode 3: the day's route
  /** Leads visited today (their stops are done, D27) and the leads behind stops, from the mirror. */
  const stopContext = async (): Promise<{ done: Set<string>; leads: Map<string, Lead> }> => {
    const today = todayLocal();
    const snap = await getSnapshot();
    return {
      done: new Set(
        snap.visits.filter((v) => pacificToday(new Date(v.visited_at)) === today).map((v) => v.lead_id),
      ),
      leads: new Map(snap.leads.map((l) => [l.id, l])),
    };
  };

  const logStopVisit = async (stop: RouteStop) => {
    try {
      const linkedId = leadIdForStop(stop, await listLeads());
      if (!linkedId && !canAutoCreateLead(stop)) {
        beginRouteLead({
          place_id: stop.place_id,
          business_type: inferBusinessType(stop.types),
          current_pos: DETECTED_TO_POS[stop.detected_pos] ?? 'unknown',
          ...(stop.signal_id ? { signal_id: stop.signal_id } : {}),
        });
        navigate(`#/leads/new?routePlace=${encodeURIComponent(stop.place_id)}`);
        return;
      }
      const leadId =
        linkedId ??
        (
          await createLead({
            business_name: stop.name,
            address: stop.address,
            place_id: googlePlaceId(stop),
            business_type: inferBusinessType(stop.types),
            current_pos: DETECTED_TO_POS[stop.detected_pos],
          })
        ).id;
      if (stop.signal_id && !stop.lead_id) {
        // a stop picked from New businesses (D80): the visit marks the signal added, behind the lead;
        // an Add + plan stop already carries its lead and queued its add
        await queueSignalPatch(stop.signal_id, { status: 'added', lead_id: leadId }, `Add · ${stop.name}`);
        kickSync();
      }
      if (stored && (leadId !== stop.lead_id || stop.signal_id)) {
        stored = {
          ...stored,
          stops: stored.stops.map((s) => {
            if (s.place_id !== stop.place_id) return s;
            const { signal_id: _marked, ...rest } = s;
            return { ...rest, lead_id: leadId };
          }),
        };
        unwatched(saveRoute(stored));
      }
      navigate(`#/log?lead=${encodeURIComponent(leadId)}&return=${encodeURIComponent('/route')}`);
    } catch (err) {
      shell.toast(friendlyError(err), { kind: 'error' });
    }
  };

  const renderStops = () => {
    if (!stored) return void renderSearch();
    closeSearch();
    mode = 'stops';
    scoutSeq++;
    const route = stored;
    shell.setTitle('Route', { subtitle: anchorLabel(route) });
    const list = h('div', { class: 'stops' });
    // Same form, same two-hop lines and lock as the search screen (CAY-115): both read twoHopState.
    liveEntry = anchorEntry(route.anchor);
    liveForm = locatedAnchor(route) ? null : meetingPointForm(route.anchor, async anchor => {
      await saveRoute({ ...(loadRoute(route.day) ?? route), anchor });
      rememberSave(anchor);
      stored = loadRoute(route.day);
      renderStops();
    });
    replaceChildren(
      host,
      banner,
      h(
        'div',
        { class: 'routehead' },
        h(
          'div',
          { class: 'routehead__text' },
          span('routehead__label mono', 'TODAY’S ROUTE'),
          h('div', { class: 'routehead__anchor' }, anchorLabel(route)),
        ),
        h(
          'div',
          { class: 'stack-h' },
          linkBtn('Plan tomorrow', '#/route/plan', { variant: 'subtle', size: 'sm' }),
          btn('Meeting point', { variant: 'subtle', size: 'sm', onClick: () => void renderSearch() }),
          btn('Edit', { variant: 'subtle', size: 'sm', onClick: () => void renderScout() }),
          btn('Clear', {
            variant: 'danger',
            size: 'sm',
            onClick: () => {
              const previous = route;
              unwatched(clearRoute());
              stored = null;
              selected.clear();
              void renderSearch();
              shell.toast('Route cleared — tap to undo', {
                duration: 8000,
                onClick: () => {
                  unwatched(saveRoute(previous));
                  if (disposed) return; // shows next time the Route tab opens
                  stored = previous;
                  if (previous.built) renderStops();
                  else void renderScout();
                },
              });
            },
          }),
        ),
      ),
      liveForm?.root ?? null,
      list,
    );
    refreshTwoHop();

    const render = async () => {
      const { done, leads } = await stopContext();
      const start = locatedAnchor(route);
      const ordered = start ? (route.planned ? keepOrder : orderStops)(start, route.stops.filter(locatedStop)) : route.stops.map(stop => ({ stop, legMeters: null }));
      let firstOpenSeen = false;
      replaceChildren(
        list,
        route.stops.length === 0 ? empty('No stops in this route.') : null,
        route.stops.some((s) => s.source === 'google' || s.location_expired)
          ? h('div', { role: 'status' }, 'Google details cannot be saved. Expired locations have no navigation; public roster doors remain available.',
            linkBtn('Rebuild from public roster', '#/route/plan')) : null,
        (start ? route.stops.filter((s) => !locatedStop(s)) : []).map((s) => h('article', { class: 'stopcard' },
          h('div', { class: 'stopcard__name' }, s.name), s.note ?? '',
          btn('Log visit', { variant: 'primary', size: 'sm', onClick: () => void logStopVisit(s) }),
        )),
        ordered.map(({ stop, legMeters }, i) => {
          const isDone = !!stop.lead_id && done.has(stop.lead_id);
          const isNext = !isDone && !firstOpenSeen && (firstOpenSeen = true);
          const lead = stop.lead_id ? leads.get(stop.lead_id) : undefined;
          return h(
            'article',
            {
              class: ['stopcard', isDone ? 'stopcard--done' : '', isNext ? 'stopcard--next' : '']
                .filter(Boolean)
                .join(' '),
            },
            h('div', { class: 'stopcard__num mono' }, isDone ? '✓' : String(i + 1)),
            h(
              'div',
              { class: 'stopcard__body' },
              stop.arrive ? h('div', { class: 'stopcard__arrive mono' }, clockText(stop.arrive)) : null,
              h('div', { class: 'stopcard__name' }, stop.name),
              stop.note ? h('div', { class: 'stopcard__note' }, stop.note) : null,
              h(
                'div',
                { class: 'stopcard__meta' },
                // the POS the rep recorded beats a website guess
                lead ? span('posbadge', POS_LABELS[lead.current_pos]) : posBadge(stop.detected_pos),
                legMeters === null ? null : span('mono muted', ` · ${formatDistance(legMeters)} ${i === 0 ? 'from start' : 'from prev'}`),
              ),
              stop.address ? h('div', { class: 'stopcard__addr' }, stop.address) : null,
              h(
                'div',
                { class: 'stopcard__actions' },
                locatedStop(stop) ? h(
                  'a',
                  {
                    class: 'btn btn--subtle btn--sm',
                    href: navigateUrl(stop),
                    target: '_blank',
                    rel: 'noopener',
                  },
                  'Navigate',
                ) : null,
                stop.call_first && stop.phone
                  ? h(
                      'a',
                      { class: 'btn btn--subtle btn--sm', href: `tel:${stop.phone.replace(/[^\d+]/g, '')}` },
                      `Call first · ${stop.phone}`,
                    )
                  : null,
                btn(isDone ? 'Log again' : 'Log visit', {
                  variant: isDone ? 'ghost' : 'primary',
                  size: 'sm',
                  onClick: () => void logStopVisit(stop),
                }),
              ),
            ),
          );
        }),
      );
    };
    void render();
    storeOff?.();
    storeOff = onStoreChange(() => void render());
  };

  // ---------------------------------------------------------------- entry
  const show = () => {
    if (stored?.built || (stored?.stops.length && !locatedAnchor(stored))) renderStops();
    else if (stored) void renderScout();
    else void renderSearch();
  };
  const onRouteChanged = () => {
    if (disposed || mode === 'scout') return;
    stored = loadRoute();
    selected.clear();
    pickFor(stored?.anchor); // a resolved anchor or another address lets the remembered pick go
    if (searchStays()) refreshTwoHop();
    else show();
  };
  window.addEventListener(ROUTE_CHANGED, onRouteChanged);
  show();

  return () => {
    disposed = true;
    window.removeEventListener(ROUTE_CHANGED, onRouteChanged);
    window.removeEventListener('online', onConnection);
    window.removeEventListener('offline', onConnection);
    authOff();
    closeSearch();
    storeOff?.();
    for (const m of markers.values()) m.setMap(null);
    markers.clear();
    anchorMarker?.setMap(null);
  };
};
